import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { imposterOnlineAvatarEmoji } from "./imposterOnlineAvatar";
import type { PartyShellPalette } from "../theme/theme";

export type ImposterOnlineRosterRow = {
  display_name?: string;
  connected?: boolean;
  is_host?: boolean;
};

type Props = {
  players: Record<string, ImposterOnlineRosterRow>;
  playerOrder: string[];
  title: string;
  emptyHint: string;
  partyPal: PartyShellPalette;
  myPlayerId?: string | null;
  youLabel?: string;
  /** Localized host suffix, e.g. "Host" / "مضيف" */
  hostLabel?: string;
  /** When set, used instead of ``display_name`` / raw id for the main line. */
  formatPlayerName?: (playerId: string, row: ImposterOnlineRosterRow | undefined) => string;
};

export function ImposterOnlinePlayersRoster({
  players,
  playerOrder,
  title,
  emptyHint,
  partyPal,
  myPlayerId,
  youLabel = "",
  hostLabel = "Host",
  formatPlayerName,
}: Props) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        mx: 2.5,
        borderRadius: 3,
        border: `1.5px solid ${partyPal.inkFaint}`,
        bgcolor: "rgba(255,255,255,0.04)",
      }}
      aria-live="polite"
    >
      <Typography sx={{ fontWeight: 800, mb: 1.5, color: partyPal.ink }}>{title}</Typography>
      {!playerOrder.length ? (
        <Typography sx={{ fontSize: 14, color: partyPal.inkDim, lineHeight: 1.5 }}>{emptyHint}</Typography>
      ) : (
        <Stack spacing={1.25}>
          {playerOrder.map((pid) => {
            const row = players[pid];
            const name = formatPlayerName
              ? formatPlayerName(pid, row)
              : (row?.display_name ?? "").trim() || pid;
            return (
              <Stack key={pid} direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: "rgba(255,255,255,0.12)",
                    fontSize: "1.35rem",
                    border: `1px solid ${partyPal.inkFaint}`,
                  }}
                  aria-hidden
                >
                  {imposterOnlineAvatarEmoji(pid)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: partyPal.ink, lineHeight: 1.3 }} noWrap>
                    {name}
                    {row?.is_host ? (
                      <Typography component="span" sx={{ fontWeight: 600, color: partyPal.hot, ml: 0.75, fontSize: 13 }}>
                        · {hostLabel}
                      </Typography>
                    ) : null}
                    {youLabel && myPlayerId && pid === myPlayerId ? (
                      <Typography component="span" sx={{ fontWeight: 600, color: partyPal.inkDim, ml: 0.75, fontSize: 14 }}>
                        {youLabel}
                      </Typography>
                    ) : null}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: row?.connected ? "#7bd88f" : "rgba(255,255,255,0.22)",
                    flexShrink: 0,
                    boxShadow: row?.connected ? "0 0 0 1px rgba(0,0,0,0.2)" : "none",
                  }}
                  title={row?.connected ? "Connected" : "Away"}
                  aria-hidden
                />
              </Stack>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
